import { useRef, useState } from "react";
import { Play, RotateCcw } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { scrollToId } from "@/App";

const POSTER = "/assets/founder/founder-poster.jpg";
const VIDEO = "/assets/founder/founder-message.mp4";
const LOGO = "/assets/founder/airobotcamstudio-logo.png";
const VIDEO_TITLE = "AIROBOTCAMSTUDIO Founder Message — Building the Future of AI Entertainment";

function FounderPlayer() {
  const videoRef = useRef(null);
  const [state, setState] = useState("poster"); // poster | playing | ended

  const start = () => {
    const v = videoRef.current;
    if (!v) return;
    const result = v.play();
    if (result) result.catch(() => setState("poster"));
  };

  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    start();
  };

  const playing = state === "playing";
  const ended = state === "ended";

  return (
    <div
      data-testid="founder-video-card"
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_0_80px_-20px_rgba(103,232,249,0.15)]"
    >
      <img
        src={POSTER}
        alt="Artur-Mihail Popescu, Founder of AIROBOTCAMSTUDIO, presenting the company vision."
        loading="lazy"
        width={960}
        height={960}
        data-testid="founder-poster"
        className={`aspect-square w-full object-cover transition-all duration-700 ${
          playing ? "opacity-0" : "opacity-85 group-hover:opacity-95"
        }`}
      />
      <video
        ref={videoRef}
        data-testid="founder-video"
        src={VIDEO}
        poster={POSTER}
        preload="metadata"
        playsInline
        controls={playing}
        title={VIDEO_TITLE}
        aria-label={VIDEO_TITLE}
        onPlay={() => setState("playing")}
        onEnded={() => setState("ended")}
        onError={() => setState("poster")}
        className={`absolute inset-0 h-full w-full bg-black object-cover transition-opacity duration-500 ${
          state === "poster" ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      />
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            data-testid="founder-play"
            onClick={ended ? replay : start}
            aria-label={ended ? `Replay: ${VIDEO_TITLE}` : `Play: ${VIDEO_TITLE}`}
            className="flex h-16 w-16 items-center justify-center rounded-full border border-cyan-300/30 bg-black/60 text-white backdrop-blur-md transition-all duration-300 hover:scale-105 hover:border-cyan-300/70 hover:text-cyan-200 sm:h-20 sm:w-20"
          >
            {ended ? <RotateCcw size={22} /> : <Play size={22} className="ml-1" />}
          </button>
        </div>
      )}
    </div>
  );
}

export default function FounderVideo() {
  return (
    <section
      id="founder"
      data-testid="founder-section"
      aria-labelledby="founder-heading"
      className="relative overflow-hidden py-16 lg:py-36"
    >
      <div className="pointer-events-none absolute left-1/2 top-16 h-[360px] w-[640px] -translate-x-1/2 rounded-full bg-cyan-400/[0.05] blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-x-16 gap-y-10 lg:grid-cols-2">
          <Reveal className="order-1 flex justify-center lg:col-start-2 lg:row-start-1 lg:justify-start">
            <img
              src={LOGO}
              alt="AIROBOTCAMSTUDIO official logo"
              loading="lazy"
              width={1254}
              height={1254}
              data-testid="founder-logo"
              className="h-auto w-36 rounded-2xl border border-white/10 sm:w-40 lg:w-44"
            />
          </Reveal>

          <Reveal className="order-2 lg:col-start-2 lg:row-start-2" delay={0.05}>
            <div className="text-center lg:text-left">
              <SectionTag>Founder Message</SectionTag>
              <h2
                id="founder-heading"
                data-testid="founder-heading"
                className="font-display mt-5 text-3xl font-light tracking-tighter text-white sm:text-4xl lg:text-5xl"
              >
                A Message from the <span className="text-cyan-300">Founder</span>
              </h2>
              <p data-testid="founder-copy" className="mt-6 text-base leading-relaxed text-[#C5CBD5]">
                We&rsquo;re building toward a new generation of AI entertainment — combining
                photorealistic AI characters, real-time video interaction and a long-term vision for
                humanoid AI.
              </p>
              <p className="mt-4 text-base leading-relaxed text-[#C5CBD5]">
                Join us as we build the future of entertainment.
              </p>
            </div>
          </Reveal>

          <Reveal className="order-3 lg:col-start-1 lg:row-start-1 lg:row-span-5" delay={0.1}>
            <FounderPlayer />
          </Reveal>

          <Reveal className="order-4 lg:col-start-2 lg:row-start-3" delay={0.15}>
            <div
              data-testid="founder-identity"
              className="border-l-2 border-cyan-300/40 pl-5 text-left"
            >
              <p className="font-display text-lg font-medium tracking-tight text-white">
                Artur-Mihail Popescu
              </p>
              <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-cyan-300/80">
                Founder &amp; Vision Lead
              </p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-white/55">
                AIROBOTCAMSTUDIO LTD
              </p>
            </div>
          </Reveal>

          <Reveal className="order-5 lg:col-start-2 lg:row-start-4" delay={0.2}>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start">
              <button
                data-testid="founder-join-cta"
                onClick={() => scrollToId("contact")}
                className="rounded-md bg-cyan-300 px-7 py-3.5 text-sm font-bold text-black transition-transform duration-300 hover:-translate-y-1 hover:bg-cyan-200"
              >
                Join the Team
              </button>
              <button
                data-testid="founder-investor-cta"
                onClick={() => scrollToId("investment")}
                className="rounded-md border border-cyan-300/30 px-7 py-3.5 text-sm font-semibold text-cyan-200/90 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-300/60 hover:text-cyan-100"
              >
                Investor Interest
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
